import { useEffect, useState, useRef, type ReactNode } from 'react'
import { onAuthStateChanged, getIdTokenResult, type User, type IdTokenResult } from 'firebase/auth'
import { auth } from '../firebase'
import { AuthContext } from '../contexts/AuthContext'
import type { CustomClaims, AuthContextType } from '../hooks/useAuth'
import { handleRedirectResult } from '../lib/auth'
import { useNavigate } from 'react-router-dom'
import { debug } from '../utils/debug'

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const navigate = useNavigate()
  const [user, setUser] = useState<User | null>(null)
  const [claims, setClaims] = useState<CustomClaims | null>(null)
  const [loading, setLoading] = useState(true)
  const tokenRefreshTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Get fresh ID token
  const getToken = async (): Promise<string | null> => {
    if (!user) return null
    try {
      const token = await user.getIdToken()
      return token
    } catch (error) {
      debug.error('Error getting ID token:', error)
      return null
    }
  }

  // Refresh claims with a delay to allow Cloud Functions to complete
  const refreshClaimsWithDelay = async (user: User, delayMs: number = 2000) => {
    // Clear any existing timeout
    if (tokenRefreshTimeoutRef.current) {
      clearTimeout(tokenRefreshTimeoutRef.current)
    }

    tokenRefreshTimeoutRef.current = setTimeout(async () => {
      try {
        debug.auth('Refreshing ID token to get updated claims...')
        await user.getIdToken(true) // Force refresh
        const idTokenResult = await getIdTokenResult(user)
        debug.auth('Updated ID token claims:', idTokenResult.claims)
        setClaims(idTokenResult.claims as CustomClaims)
      } catch (error) {
        debug.error('Error refreshing claims:', error)
      }
    }, delayMs)
  }

  useEffect(() => {
    // Handle redirect result first (for OAuth flows)
    const handleInitialAuth = async () => {
      try {
        const { user, redirectTo } = await handleRedirectResult()
        if (user && redirectTo) {
          debug.auth('Redirecting after successful OAuth to:', redirectTo)
          navigate(redirectTo, { replace: true })
        }
      } catch (error) {
        debug.error('Error handling initial redirect result:', error)
      }
    }

    handleInitialAuth()

    // TODO: Remove debug logs before production deployment
    debug.auth('Setting up auth state listener...')
    debug.auth('Environment info:', {
      isDev: import.meta.env.DEV,
      useEmulators: import.meta.env.VITE_USE_EMULATORS,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    })

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      debug.auth('Auth state changed:', user ? `User signed in: ${user.email}` : 'User signed out')

      setUser(user)

      if (user) {
        try {
          // Get ID token result to extract custom claims
          const idTokenResult: IdTokenResult = await getIdTokenResult(user)
          debug.auth('ID token claims:', idTokenResult.claims)
          setClaims(idTokenResult.claims as CustomClaims)

          // Check if user has no role assigned (new user)
          const currentClaims = idTokenResult.claims as CustomClaims
          if (!currentClaims.role || currentClaims.role === 'none') {
            debug.auth('New user detected, scheduling claims refresh...')
            // Schedule a refresh after 2 seconds to allow Cloud Function to complete
            refreshClaimsWithDelay(user, 2000)
          }
        } catch (error) {
          debug.error('Error getting ID token result:', error)
          setClaims(null)
        }
      } else {
        setClaims(null)
      }

      setLoading(false)
    })

    return () => {
      debug.auth('Cleaning up auth state listener')
      unsubscribe()

      // Clear any pending token refresh timeout
      if (tokenRefreshTimeoutRef.current) {
        clearTimeout(tokenRefreshTimeoutRef.current)
      }
    }
  }, [navigate])

  // Manual refresh function for claims
  const refreshClaims = async () => {
    if (!user) return
    try {
      debug.auth('Manually refreshing claims...')
      await user.getIdToken(true) // Force refresh
      const idTokenResult = await getIdTokenResult(user)
      debug.auth('Manually refreshed claims:', idTokenResult.claims)
      setClaims(idTokenResult.claims as CustomClaims)
    } catch (error) {
      debug.error('Error manually refreshing claims:', error)
    }
  }

  const value: AuthContextType = {
    user,
    claims,
    loading,
    isAuthenticated: !!user,
    getToken,
    refreshClaims,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
